import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function FeedbackSection() {
  const { t } = useTranslation();

  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleFeedbackSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (rating === 0) return;
    setSubmitting(true);

    // iOS: aktives Element defokussieren, um Auto-Zoom zurückzusetzen
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    // Viewport kurz auf maximum-scale=1 setzen und dann zurücksetzen
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    if (viewportMeta) {
      const original = viewportMeta.getAttribute('content') || '';
      viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1');
      setTimeout(() => viewportMeta.setAttribute('content', original), 300);
    }

    try {
      const params = new URLSearchParams();
      params.append('rating', String(rating));
      params.append('message', message);
      await fetch('https://readdy.ai/api/form/d6jb2kbcs59vsrrrjv7g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setSubmitted(true);
    } catch {
      // silent
    } finally {
      setSubmitting(false);
    }
  };

  const ratingLabels = [
    t('footer.feedbackRating1'),
    t('footer.feedbackRating2'),
    t('footer.feedbackRating3'),
    t('footer.feedbackRating4'),
    t('footer.feedbackRating5'),
  ];

  return (
    <section className="flex flex-col items-center justify-center bg-[#F0EDE6] px-4 py-8 sm:py-16 md:py-20 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#2B8FBF]/5"></div>
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#2B8FBF]/5"></div>
        <p className="absolute bottom-6 left-0 right-0 text-center text-[#D8D4CC] text-5xl md:text-8xl font-black tracking-widest select-none">
          FEEDBACK
        </p>
      </div>

      <div className="relative z-10 w-full max-w-xl mx-auto">
        {/* Label */}
        <p className="text-center text-xs font-bold tracking-[0.2em] text-[#2B8FBF] uppercase mb-2 sm:mb-3">
          {t('footer.feedbackTitle')}
        </p>

        {/* Headline */}
        <h2 className="text-center text-xl sm:text-2xl md:text-4xl font-black text-gray-900 uppercase leading-tight mb-2 sm:mb-4">
          {t('feedback.headline')}
        </h2>

        <p className="text-center text-gray-500 text-xs sm:text-sm md:text-base mb-5 sm:mb-8 md:mb-10 max-w-md mx-auto">
          {t('footer.feedbackDescription')}
        </p>

        {submitted ? (
          <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-10 flex flex-col items-center gap-4 text-center relative">
            <button
              type="button"
              onClick={() => {
                setSubmitted(false);
                setRating(0);
                setMessage('');
              }}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
              aria-label={t('common.close')}
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100">
              <i className="ri-checkbox-circle-fill text-green-500 text-4xl"></i>
            </div>
            <p className="text-gray-800 font-bold text-lg">{t('footer.feedbackSuccess')}</p>
          </div>
        ) : (
          <form
            data-readdy-form
            id="feedback-section-form"
            onSubmit={handleFeedbackSubmit}
            className="bg-white rounded-3xl shadow-xl p-4 sm:p-6 md:p-10 space-y-4 sm:space-y-6"
          >
            {/* Stars */}
            <div className="flex flex-col items-center gap-2 sm:gap-3">
              <div className="flex items-center gap-2 md:gap-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHovered(star)}
                    onMouseLeave={() => setHovered(0)}
                    className="w-9 h-9 md:w-12 md:h-12 flex items-center justify-center cursor-pointer transition-transform hover:scale-125"
                  >
                    <i
                      className={`text-2xl md:text-4xl transition-colors ${
                        star <= (hovered || rating)
                          ? 'ri-star-fill text-yellow-400'
                          : 'ri-star-line text-gray-300'
                      }`}
                    ></i>
                  </button>
                ))}
              </div>
              <div className="h-4 sm:h-5">
                {(hovered || rating) > 0 && (
                  <span className="text-xs sm:text-sm font-semibold text-gray-600">
                    {ratingLabels[(hovered || rating) - 1]}
                  </span>
                )}
              </div>
            </div>

            {/* Textarea */}
            <div>
              <textarea
                name="message"
                value={message}
                onChange={(e) => {
                  if (e.target.value.length <= 500) setMessage(e.target.value);
                }}
                placeholder={t('footer.feedbackPlaceholder')}
                rows={3}
                maxLength={500}
                className="w-full bg-gray-50 border border-gray-200 text-gray-800 px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2B8FBF] resize-none placeholder-gray-400"
                style={{ fontSize: '16px' }}
              />
              <div className="flex justify-end mt-1">
                <span className="text-gray-400 text-xs">{message.length}/500</span>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={rating === 0 || submitting}
              className="w-full bg-[#2B8FBF] hover:bg-[#2480ad] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold py-3 rounded-full transition-all cursor-pointer whitespace-nowrap"
            >
              {submitting ? t('footer.feedbackSubmitting') : t('footer.feedbackSubmit')}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
